import type { Scene } from './types';
import { ensureBackdrop, drawEffects, drawOneBlast, drawSmokeScreen, drawOneCasing, drawOneMuzzlePuff, drawDamages, drawFlashbangs, flashbangWash } from './effects';
import { drawShadow, drawFighter, drawFighterWeapon, drawFighterLabel, actionBadge, fighterBadge } from './fighters';
import { drawHpBar, drawKillFeed } from './hud';
import { drawRing, RING_GREEN, RING_RED, RING_SMOKE } from './markers';
import { aimTargetOf, type BattleEngineOpts, type PlaybackState } from './playbackState';

export interface FieldLayout { fieldX: number; fieldW: number; offY: number; scale: number; }

export const computeField = (cw: number, ch: number, safeBottom: number, W: number, H: number): FieldLayout => {
  const availH = Math.max(1, ch - safeBottom);
  const scale = Math.min(cw / W, availH / H);
  const fieldW = W * scale;
  const fieldX = (cw - fieldW) / 2;
  const offY = (availH - H * scale) / 2;
  return { fieldX, fieldW, offY, scale };
};

export const renderScene = (
  sc: Scene, opts: BattleEngineOpts, n: number,
  cw: number, ch: number, dpr: number, safeTop: number,
  field: FieldLayout, replaying: boolean, state: PlaybackState,
): void => {
  const { ctx } = sc;
  const backdrop = ensureBackdrop(sc, cw, ch, dpr, field.fieldX, field.offY, field.scale);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  if (backdrop) ctx.drawImage(backdrop, 0, 0);
  else ctx.clearRect(0, 0, cw * dpr, ch * dpr);

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.save();
  ctx.translate(field.fieldX, field.offY);
  ctx.scale(field.scale, field.scale);

  const youId = opts.replay ? null : opts.getYouId();
  const target = opts.replay ? null : aimTargetOf(youId, youId, replaying, state, opts);
  const ordered = [...sc.fighters].sort((a, b) => a.y - b.y);
  const youFighter = youId ? sc.byId[youId] : null;
  const youAlive = !youFighter?.dead;
  const youTeam = youFighter?.team;
  const youBadge = youFighter ? actionBadge(sc, opts.getChosenAction()) : null;

  for (const f of ordered) drawShadow(sc, f, n);
  if (youFighter && !youFighter.dead) drawRing(sc, youFighter, n, RING_GREEN);
  if (target && youAlive && target !== youId) {
    const tf = sc.byId[target];
    if (tf && (!tf.dead || replaying)) {
      drawRing(sc, tf, n, tf.team === youTeam ? RING_SMOKE : RING_RED);
    }
  }
  const world: { y: number; draw: () => void }[] = [];
  for (const f of ordered) world.push({ y: f.y, draw: () => { drawFighter(sc, f, n); drawFighterWeapon(sc, f, n, state); } });
  for (const [id, s] of state.smokeScreens) world.push({ y: s.y, draw: () => drawSmokeScreen(sc, id, s, n, state) });
  for (const bl of sc.blasts) world.push({ y: bl.groundY, draw: () => drawOneBlast(sc, bl, n) });
  for (const c of sc.casings) world.push({ y: c.groundY, draw: () => drawOneCasing(sc, c, n) });
  for (const p of sc.muzzlePuffs) world.push({ y: p.oy, draw: () => drawOneMuzzlePuff(sc, p, n) });
  world.sort((a, b) => a.y - b.y);
  for (const it of world) it.draw();
  drawFlashbangs(sc, n);
  for (const f of ordered) {
    const badge = (f.id === youId && youBadge) ? youBadge : fighterBadge(sc, f);
    drawFighterLabel(sc, f, f.id === youId, badge);
  }
  drawEffects(sc, n);
  drawDamages(sc, n);
  drawKillFeed(sc, n);
  ctx.restore();

  const flashWash = flashbangWash(sc, n);
  if (flashWash > 0) {
    ctx.save();
    ctx.globalAlpha = flashWash;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cw, ch);
    ctx.restore();
  }

  drawHpBar(sc, field.fieldX, field.fieldW, safeTop);
};
